<template>
  <Admin class="bg-gray-100">
    <v-main>
      <v-container fluid>
        <v-card class="rounded-xl min-h-80">
          <template v-if="!selectedCompany.isDemo">
            <v-card-text>
              <!-- Breadcrumbs -->
              <v-breadcrumbs :items="breadcrumbItems">
                <template v-slot:divider>
                  <v-icon>mdi-chevron-right</v-icon>
                </template>
                <template v-slot:item="{ item }">
                  <v-breadcrumbs-item
                    :to="item.to"
                    :disabled="item.disabled"
                    :exact="true"
                  >
                    {{ item.text }}
                  </v-breadcrumbs-item>
                </template>
              </v-breadcrumbs>

              <!-- Page Title -->
              <h1 class="mx-5 tw-font-bold tw-text-xl tw-text-black tw-tracking-wide tw-mb-4">
                จัดการเมนูบริษัท
              </h1>

              <!-- Company Table -->
              <v-card class="mx-5 elevation-0">
                <v-data-table
                  :headers="headers"
                  :items="companies"
                  :items-per-page="-1"
                  class="company-table"
                >
                  <!-- Logo Column -->
                  <template v-slot:item.logo="{ item }">
                    <v-avatar
                      :color="item.company.imageLogoUrl ? 'transparent' : '#F5F5F5'"
                      size="30"
                      class="mr-3 logo-container"
                    >
                      <img
                        v-if="item.company.imageLogoUrl"
                        :src="getCompanyLogoUrl(item)"
                        :alt="item.company.nameTH"
                        class="company-logo"
                      />
                      <span v-else-if="item.company.nameEN" class="company-initial">
                        {{ getCompanyInitial(item) }}
                      </span>
                    </v-avatar>
                  </template>

                  <!-- Company Name Column -->
                  <template v-slot:item.name="{ item }">
                    {{ item.company.nameTH }}
                  </template>

                  <!-- Visibility Toggle Column -->
                  <template v-slot:item.isVisible="{ item }">
                    <v-switch
                      v-if="companies.length > 1"
                      v-model="companyVisibility[item.id]"
                      @change="handleVisibilityChange(item.id)"
                      color="primary"
                      hide-details
                      class="ma-2"
                      :label="companyVisibility[item.id] ? 'แสดง' : 'ซ่อน'"
                    ></v-switch>
                  </template>
                </v-data-table>
              </v-card>

              <!-- Save Button -->
              <v-row class="mt-4">
                <v-col cols="12" class="text-right">
                  <v-btn color="primary" @click="saveSettings" :loading="isSaving">
                    <v-progress-circular
                      v-if="isSaving"
                      indeterminate
                      color="green"
                      style="margin-right: 10px;"
                    ></v-progress-circular>
                    <v-icon left class="tw-text-2xl">mdi-content-save</v-icon>
                    บันทึกการตั้งค่า
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </template>

          <!-- No Permission Message -->
          <div v-else class="flex items-center justify-center h-[200px] text-gray-500">
            No Permission
          </div>

          <!-- Snackbar for Notifications -->
          <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" top>
            {{ snackbar.message }}
          </v-snackbar>
        </v-card>
      </v-container>
    </v-main>
  </Admin>
</template>

<script>
import Admin from "../../components/admin/admin-main-template";
import { mapState } from "vuex";

export default {
  name: "CompanySettings",

  components: {
    Admin,
  },

  data() {
    return {
      breadcrumbItems: [
        {
          text: "ตั้งค่า",
          disabled: false,
          to: { name: "Company.Settings" },
        },
        {
          text: "ปรับแต่งรายการ",
          disabled: true,
        },
      ],
      headers: [
        { text: 'โลโก้', value: 'logo', sortable: false, width: '80' },
        { text: 'ชื่อบริษัท', value: 'name', sortable: false, width: '400' },
        { text: 'การแสดงผล', value: 'isVisible', sortable: false }
      ],
      companyVisibility: {},
      isSaving: false,
      snackbar: {
        show: false,
        message: '',
        color: '',
      },
    };
  },

  computed: {
    ...mapState({
      selectedCompany: (state) => state.companies.selectedCompany,
      companies: (state) => state.companies.list,
      user: (state) => state.user.userData,
    }),
    STORAGE_KEY() {
      return this.user?.email ? `companyVisibilitySettings_${this.user.email}` : null;
    },
  },

  methods: {
    getDeviceRatio() {
      return window.devicePixelRatio;
    },

    getCompanyLogoUrl(company) {
      return `${company.company.imageLogoUrl}=s${Math.round(30 * this.getDeviceRatio())}`;
    },

    getCompanyInitial(company) {
      return company.company.nameEN?.[0]?.toUpperCase() ?? '';
    },

    getCompanyList() {
      this.$store.dispatch('companies/getList');
    },

    initializeVisibilitySettings() {
      // Only attempt to load settings if STORAGE_KEY is defined
      if (this.STORAGE_KEY) {
        const savedSettings = localStorage.getItem(this.STORAGE_KEY);
        if (savedSettings) {
          this.companyVisibility = JSON.parse(savedSettings);
        } else {
          // Initialize all companies as visible by default for new users
          this.companies.forEach(company => {
            this.$set(this.companyVisibility, company.id, true);
          });
        }
      }
    },

    handleVisibilityChange(companyId) {
      this.$set(this.companyVisibility, companyId, this.companyVisibility[companyId]);
    },

    async saveSettings() {
      try {
        this.isSaving = true;

        // Save to localStorage with user-specific key
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.companyVisibility));

        // Show success snackbar
        this.snackbar = {
          show: true,
          message: 'บันทึกการตั้งค่าเรียบร้อยแล้ว',
          color: 'success',
        };

        // Delay before refreshing to ensure the snackbar is visible briefly
        setTimeout(() => window.location.reload(), 500);

      } catch (error) {
        console.error('Error saving settings:', error);

        // Show error snackbar
        this.snackbar = {
          show: true,
          message: 'เกิดข้อผิดพลาดในการบันทึกการตั้งค่า',
          color: 'error',
        };
      } finally {
        this.isSaving = false;
      }
    },
  },

  created() {
    this.getCompanyList();
  },

  mounted() {
    // Wait for `user.email` to be available before initializing settings
    if (this.STORAGE_KEY) {
      this.initializeVisibilitySettings();
    }
  },

  watch: {
    companies: {
      handler() {
        // Reinitialize when `companies` data updates
        this.initializeVisibilitySettings();
      },
      immediate: true,
    },
    STORAGE_KEY(newKey) {
      // Reinitialize settings when `STORAGE_KEY` is defined or changes
      if (newKey) {
        this.initializeVisibilitySettings();
      }
    },
  },
};
</script>

<style lang="css" scoped>

.company-table .logo-container .company-logo {
      object-fit: contain;
      width: 100%;
      height: 100%;
    }

</style>
